/**
 * Pricing calculator that respects admin-configured rules.
 *
 *  Rule formulas:
 *   - max_plus_flat        : max(prices) preserved + flat EUR added (one-time).
 *                            Other matching services priced at 0 (bundled).
 *   - max_plus_min_coef    : max(prices) preserved + min(prices) * coef.
 *                            Other matching services (3rd, 4th…) priced at price * coef.
 *   - max_plus_others_coef : max(prices) preserved + ALL other matching * coef.
 *   - addon_coef           : addon service alone with another non-addon service →
 *                            addon's price = price * coef. Base goes to bulk tiers.
 *   - addon_flat           : addon's price = flat EUR. Base goes to bulk tiers.
 *   - bulk_tier            : applied positionally (rule_0_1 = 2nd, rule_0_2 = 3rd, rule_0_3 = 4th+)
 *   - rounding             : applied to the FINAL total at the end.
 *
 * Selected = [{ id, name, price, ... }]
 * Rules    = list of pricing rules from /api/config/pricing-rules
 *
 * Returns:
 *  { items: [{ id, name, originalPrice, finalPrice, discount, ruleApplied }],
 *    subtotal, originalTotal, savings,
 *    appliedRules: ['rule_7', 'rule_0_1', ...] }
 */

const matchesAny = (name, keywords) => {
  if (!keywords || !keywords.length) return false;
  const n = (name || '').toLowerCase();
  return keywords.some((kw) => kw && n.includes(String(kw).toLowerCase()));
};

export function calculatePrice(selectedOptions, rules) {
  const items = (selectedOptions || []).map((o) => ({
    id: o.id,
    name: o.name,
    icon: o.icon,
    instruction: o.instruction,
    image: o.image,
    originalPrice: Number(o.price) || 0,
    finalPrice: Number(o.price) || 0,
    discount: 0,
    ruleApplied: null,
  }));
  if (items.length === 0) {
    return { items: [], subtotal: 0, originalTotal: 0, savings: 0, appliedRules: [] };
  }

  const safeRules = Array.isArray(rules) ? rules : [];
  const comboRules = safeRules
    .filter((r) => r.category === 'combo' && (r.formula || '').startsWith('max_plus_'))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const addonRules = safeRules
    .filter((r) => r.category === 'combo' && (r.formula || '').startsWith('addon_'));
  const bulkByKey = Object.fromEntries(
    safeRules.filter((r) => r.category === 'bulk_discount').map((r) => [r.key, r])
  );

  const consumed = new Set();
  const appliedRules = new Set();

  // 1) Apply max_plus_* combo rules first (they need ≥2 matching services)
  for (const rule of comboRules) {
    const matched = items.filter(
      (i) => !consumed.has(i.id) && matchesAny(i.name, rule.services)
    );
    if (matched.length < 2) continue;
    matched.sort((a, b) => b.originalPrice - a.originalPrice);
    const [head, ...tail] = matched;

    head.finalPrice = head.originalPrice;
    head.discount = 0;
    head.ruleApplied = rule.key;
    consumed.add(head.id);

    if (rule.formula === 'max_plus_flat') {
      // First "other" gets the flat EUR add-on; further ones bundled at 0
      tail.forEach((it, i) => {
        const flat = Number(rule.value) || 0;
        it.finalPrice = i === 0 ? flat : 0;
        it.discount =
          it.originalPrice > 0 ? Math.round((1 - it.finalPrice / it.originalPrice) * 100) : 0;
        it.ruleApplied = rule.key;
        consumed.add(it.id);
      });
    } else if (rule.formula === 'max_plus_min_coef') {
      // 2nd item → min × coef; 3rd+ in same combo → price × coef as well
      tail.forEach((it) => {
        const coef = Number(rule.value) || 0;
        it.finalPrice = Math.round(it.originalPrice * coef);
        it.discount = Math.round((1 - coef) * 100);
        it.ruleApplied = rule.key;
        consumed.add(it.id);
      });
    } else if (rule.formula === 'max_plus_others_coef') {
      tail.forEach((it) => {
        const coef = Number(rule.value) || 0;
        it.finalPrice = Math.round(it.originalPrice * coef);
        it.discount = Math.round((1 - coef) * 100);
        it.ruleApplied = rule.key;
        consumed.add(it.id);
      });
    }
    appliedRules.add(rule.key);
  }

  // 2) Addon rules: addon services priced by coef/flat ONLY when ≥1 non-addon service is also selected
  for (const rule of addonRules) {
    const addons = items.filter(
      (i) => !consumed.has(i.id) && matchesAny(i.name, rule.services)
    );
    if (addons.length === 0) continue;
    const hasBase = items.some(
      (i) => !consumed.has(i.id) && !matchesAny(i.name, rule.services)
    );
    if (!hasBase) continue;
    addons.forEach((it) => {
      if (rule.formula === 'addon_flat' || rule.unit === 'eur') {
        const flat = Number(rule.value) || 0;
        it.finalPrice = flat;
        it.discount =
          it.originalPrice > 0 ? Math.round((1 - flat / it.originalPrice) * 100) : 0;
      } else {
        const coef = Number(rule.value) || 0;
        it.finalPrice = Math.round(it.originalPrice * coef);
        it.discount = Math.round((1 - coef) * 100);
      }
      it.ruleApplied = rule.key;
      consumed.add(it.id);
    });
    appliedRules.add(rule.key);
  }

  // 3) Bulk tier discounts for everything still un-consumed.
  //
  // IMPORTANT: bulk-tier coefficient is keyed off the ABSOLUTE position
  // of each item in the price-desc-sorted list of ALL selected services,
  // NOT the position within the not-yet-consumed remainder. The admin
  // rule text reads "2-я выбранная услуга (сортировка по убыванию цены)"
  // — i.e. the second-most-expensive selected service overall. So if
  // combo rules consume e.g. positions #3 and #4, the next non-consumed
  // item at position #5 must use rule_0_3 (0.3), not rule_0_2.
  const absPos = new Map();
  [...items]
    .sort((a, b) => b.originalPrice - a.originalPrice)
    .forEach((it, idx) => absPos.set(it.id, idx + 1));

  const remaining = items.filter((i) => !consumed.has(i.id));

  remaining.forEach((it) => {
    const pos = absPos.get(it.id) || 1;
    if (pos === 1) {
      // Most expensive selected service — full price, no rule applied.
      it.finalPrice = it.originalPrice;
      it.discount = 0;
      return;
    }
    const ruleKey = pos === 2 ? 'rule_0_1' : pos === 3 ? 'rule_0_2' : 'rule_0_3';
    const rule = bulkByKey[ruleKey];
    const coef = rule ? Number(rule.value) : pos === 2 ? 0.5 : pos === 3 ? 0.45 : 0.3;
    it.finalPrice = Math.round(it.originalPrice * coef);
    it.discount = Math.round((1 - coef) * 100);
    it.ruleApplied = ruleKey;
    appliedRules.add(ruleKey);
  });

  const subtotal = items.reduce((s, i) => s + (Number(i.finalPrice) || 0), 0);
  const originalTotal = items.reduce((s, i) => s + (Number(i.originalPrice) || 0), 0);

  // Sort items by original price descending for display
  items.sort((a, b) => b.originalPrice - a.originalPrice);

  return {
    items,
    subtotal,
    originalTotal,
    savings: Math.max(0, originalTotal - subtotal),
    appliedRules: [...appliedRules],
  };
}

export function getRoundingStep(rules) {
  const r = (Array.isArray(rules) ? rules : []).find((x) => x.key === 'rounding_final');
  return r && Number(r.value) > 0 ? Number(r.value) : 0;
}

export function applyRounding(price, step) {
  if (!step || step <= 0) return Math.round(price);
  return Math.round(price / step) * step;
}
